import { CommonModule } from "@angular/common";
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from "@angular/core";
import Chart from "chart.js/auto";
import type { ChartConfiguration, ChartDataset } from "chart.js";
import { Widget } from "../../../dashboard/models/dashboard.model";
import { EntityMetadata } from "../../../../shared/store/store-types.model";

// UI (header + filters)
import { SharedModule } from "../../../../shared/shared.module";
import { PopoverModule } from "../../../../shared/modules/popover/popover.module";
import { TiersFacadeService } from "../../services/tiers/tiers-facade.service";
import { ArticlesInfosFacadeService } from "../../../articles/services/article-infos/article-infos-facade.service";
import { groupBy, sum } from "../../../articles/services/stats.util";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { parseNumber } from "../../../../shared/utils/number-utils.service";
import { DialogsService } from "../../../../shared/services/dialogs/dialogs.service";
import {
  AchatArticleRow,
  AchatArticlesDialogComponent,
  AchatArticlesDialogData,
  AchatCommandeDetail,
  AchatLotDetail,
} from "./achat-articles-dialog.component";
import { toDayKey } from "../../../articles/services/stats.util";

type Mode = "CA" | "VOLUME";
type ViewType = "PIE" | "LIST";
type SortBy = "CA" | "VOLUME" | "ALPHA";

@Component({
  selector: "vl-achats-articles-par-tiers",
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    PopoverModule,
    ChipsComponent,
    WidgetTitleComponent,
    ChipsToggleComponent,
  ],
  templateUrl: "./achat-articles-par-tiers.component.html",
  styleUrls: ["./achat-articles-par-tiers.component.scss"],
  host: { class: "widget-component" },
})
export class AchatsArticlesParTiersComponent {
  private readonly articleInfos = inject(ArticlesInfosFacadeService);
  private readonly tiersFacade = inject(TiersFacadeService);
  private readonly dialogsService = inject(DialogsService);

  stats = this.tiersFacade.getState().stats;
  widget = input<Widget>();
  metadata = input<EntityMetadata>();

  // Canvas optionnel (évite NG0951 quand le canvas n’est pas rendu)
  canvasRef = viewChild<ElementRef<HTMLCanvasElement>>("achatChart");
  chart?: Chart<"doughnut", number[], string>;

  // Légende externe
  legendLabels = signal<
    { label: string; percentage: string; color: string }[] | null
  >(null);

  // Toggles
  viewMode = signal<Mode>("CA");
  viewType = signal<ViewType>("PIE");
  sortBy = signal<SortBy>("CA");

  elementsMode = [
    { name: "CA", value: "CA" },
    { name: "Volume", value: "VOLUME" },
  ];
  elementsView = [
    { icon: "pie_chart", value: "PIE" },
    { icon: "list", value: "LIST" },
  ];
  elementsSort = [
    { name: "Tri CA", value: "CA" },
    { name: "Tri Volume", value: "VOLUME" },
    { name: "Tri Alpha", value: "ALPHA" },
  ];

  // Filtres
  topCount = signal<number>(12);
  search = signal<string>("");

  // Formatters
  private fmtEUR = new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });
  private fmtINT = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 });

  /** Options de tiers (fournisseurs) extraites des réceptions */
  tiersOptions = computed<{ name: string; value: string }[]>(() => {
    const recs = (this.stats()?.receptions ?? []) as any[];
    const map = new Map<string, string>();
    for (const r of recs) {
      const id = String(r?.commandeLigne?.commande?.tiersId ?? "");
      if (!id) continue;
      if (!map.has(id)) {
        const name =
          r?.commandeLigne?.commande?.tiers?.denomination ??
          r?.commandeLigne?.commande?.tiers?.raisonSociale ??
          id;
        map.set(id, name);
      }
    }
    return Array.from(map.entries())
      .map(([value, name]) => ({ value, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "fr"));
  });

  // Agrégations par ARTICLE — filtrées par tiers sélectionné
  private baseRows = computed(() => {
    const recs = (this.stats()?.receptions ?? []) as any[];
    if (!recs?.length) return [];

    const sel = this.stats()?.tiers?.id;
    if (!sel) return [];

    // filtre par tiers
    const filt = recs.filter(
      (r: any) => String(r?.commandeLigne?.commande?.tiersId ?? "") === sel,
    );

    // groupBy article
    const g = groupBy(
      filt,
      (r: any) => r?.commandeLigne?.articleId ?? "Inconnu",
    );

    return Object.entries(g).map(([articleId, es]) => {
      // label article via articleInfos
      const denom =
        this.articleInfos.getByArticleId(articleId as string)?.()
          ?.denomination ?? String(articleId);

      // qty en UG (division par coefficient UG)
      const qty = sum(
        es.map((e: any) => {
          const coef = parseNumber(
            e?.commandeLigne?.coefficientUniteGestion || 1,
          );
          const q = parseNumber(e?.quantite ?? e?.qty ?? 0);
          return q / (coef || 1);
        }),
      );

      // CA = somme(qUG * PU)
      const amount = es.reduce((a: number, e: any) => {
        const coef = parseNumber(
          e?.commandeLigne?.coefficientUniteGestion || 1,
        );
        const qUG = parseNumber(e?.quantite ?? e?.qty ?? 0) / (coef || 1);
        const pu = parseNumber(e?.prixUnitaire ?? 0);
        return a + qUG * pu;
      }, 0);

      return { key: articleId, label: denom, qty, amount };
    });
  });

  // Recherche
  private filtered = computed(() => {
    const q = this.search().trim().toLowerCase();
    if (!q) return this.baseRows();
    return this.baseRows().filter((r) => r.label?.toLowerCase?.().includes(q));
  });

  // Tri
  private sorted = computed(() => {
    const by = this.sortBy();
    const arr = [...this.filtered()];
    arr.sort((a, b) => {
      switch (by) {
        case "VOLUME":
          return b.qty - a.qty;
        case "ALPHA":
          return a.label.localeCompare(b.label, "fr");
        case "CA":
        default:
          return b.amount - a.amount;
      }
    });
    return arr;
  });

  // Top N
  maxRows = computed(() => this.filtered().length);
  private top = computed(() => {
    const n = Math.max(1, Math.min(this.topCount(), this.maxRows()));
    return this.sorted().slice(0, n);
  });

  // ===== Totaux & « Autres » =====
  private totalAll = computed(() => {
    const mode = this.viewMode();
    return this.filtered().reduce(
      (a, x) => a + (mode === "CA" ? x.amount : x.qty),
      0,
    );
  });
  private totalTopOnly = computed(() => {
    const mode = this.viewMode();
    return this.top().reduce(
      (a, x) => a + (mode === "CA" ? x.amount : x.qty),
      0,
    );
  });
  private othersValue = computed(() =>
    Math.max(0, this.totalAll() - this.totalTopOnly()),
  );
  private hasOthers = computed(
    () =>
      this.filtered().length >
      Math.max(1, Math.min(this.topCount(), this.maxRows())),
  );

  // Données (PIE + LIST)
  private pieLabels = computed(() => {
    const labs = this.top().map((x) => x.label);
    if (this.hasOthers()) labs.push("Autres");
    return labs;
  });

  // ⚠️ public pour empty state dans le template
  values = computed(() => {
    const vals = this.top().map((x) =>
      this.viewMode() === "CA" ? x.amount : x.qty,
    );
    if (this.hasOthers()) vals.push(this.othersValue());
    return vals;
  });

  // Totaux pour affichage LIST
  private total = computed(() => this.values().reduce((a, b) => a + b, 0)); // = totalAll (inclut « Autres »)
  totalTop = () => this.totalTopOnly();
  totalTopPct = () => {
    const all = this.totalAll();
    return all > 0 ? (this.totalTopOnly() / all) * 100 : 0;
  };

  listRows = computed(() => {
    const totAll = this.total() || 0; // total global (inclut « Autres »)
    const mode = this.viewMode();
    return this.top().map((x) => {
      const value = mode === "CA" ? x.amount : x.qty;
      const pct = totAll > 0 ? (value / totAll) * 100 : 0;
      return { key: x.key, label: x.label, value, pct };
    });
  });

  // ===== Palette dynamique =====
  private makeColors(n: number): string[] {
    const base = [140, 210, 260, 275, 40, 0, 195, 220];
    const out: string[] = [];
    for (let i = 0; i < n; i++) {
      const h =
        base[i % base.length] +
        (i >= base.length ? 12 * Math.floor(i / base.length) : 0);
      out.push(`hsl(${h % 360} 85% 55%)`);
    }
    return out;
  }

  private colors = computed(() =>
    this.values().length ? this.makeColors(this.values().length) : [],
  );

  // ===== Helpers =====
  private arrEq<T>(a?: T[] | null, b?: T[] | null): boolean {
    if (!a && !b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }

  constructor() {
    // Init sélection tiers
    effect(
      () => {
        this.stats(); // refresh options when stats change
        const opts = this.tiersOptions();
        const sel = this.stats()?.tiers?.id;
        const exists = !!opts.find((o) => o.value === sel);
        if (!exists) {
          const next = opts[0]?.value ?? "";
        }
      },
      { allowSignalWrites: true },
    );

    // Clamp du slider (reset initial mesuré)
    effect(
      () => {
        // Reset raisonné sur changement de tiers OU stats
        this.stats();
        const max = untracked(() => this.maxRows()) || 1;
        this.topCount.set(Math.min(10, max));
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        // Clamp quand maxRows évolue
        const max = this.maxRows() || 1;
        this.topCount.update((v) => Math.min(Math.max(1, v || 1), max));
      },
      { allowSignalWrites: true },
    );

    // MAJ chart live + légende externe
    effect(
      () => {
        if (this.viewType() !== "PIE") return;
        const canvas = this.canvasRef();
        if (!canvas || !this.chart) return;

        const labels = this.pieLabels();
        const values = this.values();
        const ds = this.chart.data.datasets[0] as ChartDataset<
          "doughnut",
          number[]
        >;
        const newLabel = this.viewMode() === "CA" ? "CA" : "Unités";

        const prevLabels = (this.chart.data.labels ?? []) as string[];
        const prevValues = (ds.data ?? []) as number[];

        this.chart.data.labels = labels;
        ds.data = values;
        ds.label = newLabel;

        if (!this.arrEq(values, prevValues)) {
          ds.backgroundColor = this.colors();
        }

        if (
          !this.arrEq(labels, prevLabels) ||
          !this.arrEq(values, prevValues)
        ) {
          this.chart.update(); // trigger generateLabels -> maj legendLabels
        }
      },
      { allowSignalWrites: true },
    );

    // (Re)build chart quand données + canvas dispo + vue PIE
    effect(
      () => {
        if (this.viewType() !== "PIE") {
          this.destroyChart(true);
          return;
        }
        const canvas = this.canvasRef();
        if (!canvas) {
          this.destroyChart(true);
          return;
        }
        if (!this.values().length) {
          this.destroyChart(true);
          return;
        }
        this.buildChart();
      },
      { allowSignalWrites: true },
    );
  }

  private destroyChart(clearLegend = false) {
    if (clearLegend) this.legendLabels.set([]);
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }

  private buildChart() {
    const canvas = this.canvasRef();
    if (!canvas) return;
    const ctx = canvas.nativeElement.getContext("2d");
    if (!ctx) return;

    this.destroyChart(true);

    const config: ChartConfiguration<"doughnut", number[], string> = {
      type: "doughnut",
      data: {
        labels: this.pieLabels(),
        datasets: [
          {
            label: this.viewMode() === "CA" ? "CA" : "Unités",
            data: this.values(),
            backgroundColor: this.colors(),
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        resizeDelay: 120,
        plugins: {
          legend: {
            display: true, // on garde pour déclencher generateLabels
            position: "right",
            labels: {
              usePointStyle: true,
              generateLabels: (c) => {
                const data = c.data as any;
                const labels = (data.labels || []) as string[];
                const values = (data.datasets?.[0]?.data || []) as number[];
                const colors = (data.datasets?.[0]?.backgroundColor ||
                  []) as string[];
                const total = values.reduce((a, b) => +a + +b, 0);
                const out = labels.map((lbl, i) => {
                  const val = +values[i] || 0;
                  const pct =
                    total > 0 ? ((val / total) * 100).toFixed(0) : "0";
                  return {
                    label: lbl,
                    percentage: pct,
                    color: String(colors[i] ?? "#999"),
                  };
                });
                this.legendLabels.set(out);
                return []; // masque la légende native
              },
            },
          },
          tooltip: {
            bodyFont: { size: 12 },
            titleFont: { size: 12 },
            callbacks: {
              label: (ctx) => {
                const val = ctx.parsed as number;
                const isCA = this.viewMode() === "CA";
                return ` ${ctx.label}: ${isCA ? this.fmtEUR.format(val) : this.fmtINT.format(val)}`;
              },
            },
          },
          title: { display: false },
        },
        layout: { padding: { top: 6, right: 6, bottom: 6, left: 6 } },
      },
    };
    this.chart = new Chart<"doughnut", number[], string>(ctx, config);
  }

  formatValue(v: number): string {
    return this.viewMode() === "CA"
      ? this.fmtEUR.format(v)
      : this.fmtINT.format(v);
  }

  /** Construit les données détaillées (article → commande → lot) pour le dialog "Voir tout". */
  openFullList() {
    const recs = (this.stats()?.receptions ?? []) as any[];
    const tiersId = this.stats()?.tiers?.id;
    if (!recs.length || !tiersId) return;

    const filt = recs.filter(
      (r: any) => String(r?.commandeLigne?.commande?.tiersId ?? "") === String(tiersId),
    );
    if (!filt.length) return;

    // Accumulateurs : article → commande → lot
    type LotAcc = {
      lotId: string | null;
      lotNumero: string;
      coefUG: number;
      qtyUG: number;
      qtyUA: number;
      coutTotal: number;
    };
    type CommandeAcc = {
      commandeId: string;
      numero: string;
      dateKey: string;
      coefUG: number;
      qtyUG: number;
      qtyUA: number;
      coutTotal: number;
      lots: Map<string, LotAcc>;
    };
    type ArticleAcc = {
      articleId: string;
      uniteGestion: string;
      uniteAchat: string;
      commandes: Map<string, CommandeAcc>;
    };

    const map = new Map<string, ArticleAcc>();

    for (const r of filt as any[]) {
      const articleId: string = r?.commandeLigne?.articleId ?? "inconnu";
      const coefUG = parseNumber(r?.commandeLigne?.coefficientUniteGestion ?? 1) || 1;
      // Source de vérité : champs explicites de la réception
      const qtyUA = parseNumber(r?.quantite ?? 0);
      const qtyUG = parseNumber(r?.quantiteUniteGestion ?? qtyUA * coefUG);
      const puUA = parseNumber(r?.prixUnitaire ?? 0); // prix €/UA (copié depuis commandeLigne.prix)
      const cout = qtyUA * puUA; // cohérent (qtyUA × €/UA = €)

      const infos = this.articleInfos.getByArticleId(articleId)?.();
      const uniteGestion = (infos?.unite as any)?.denomination || "UG";
      const rawUA = String(r?.commandeLigne?.uniteGestion ?? "").trim();
      const uniteAchat = rawUA && rawUA !== "UG" ? rawUA : uniteGestion;

      const commandeId = String(r?.commandeId ?? r?.commandeLigne?.commandeId ?? "inconnu");
      const numero = r?.commandeLigne?.commande?.numero ?? commandeId;
      const dateKey = toDayKey(r?.date) ?? "";

      const lotIdRaw: string | null = r?.lotId || r?.lot?.id || null;
      const lotKey = lotIdRaw || `__nolot_${r?.lot?.numero ?? "?"}`;
      const lotNumero = r?.lot?.numero ?? "";

      if (!map.has(articleId)) {
        map.set(articleId, { articleId, uniteGestion, uniteAchat, commandes: new Map() });
      }
      const art = map.get(articleId)!;

      if (!art.commandes.has(commandeId)) {
        art.commandes.set(commandeId, {
          commandeId, numero, dateKey,
          coefUG,
          qtyUG: 0, qtyUA: 0, coutTotal: 0,
          lots: new Map(),
        });
      }
      const cmd = art.commandes.get(commandeId)!;
      cmd.qtyUG += qtyUG;
      cmd.qtyUA += qtyUA;
      cmd.coutTotal += cout;
      if (dateKey && (!cmd.dateKey || dateKey > cmd.dateKey)) cmd.dateKey = dateKey;

      if (!cmd.lots.has(lotKey)) {
        cmd.lots.set(lotKey, {
          lotId: lotIdRaw, lotNumero, coefUG,
          qtyUG: 0, qtyUA: 0, coutTotal: 0,
        });
      }
      const lot = cmd.lots.get(lotKey)!;
      lot.qtyUG += qtyUG;
      lot.qtyUA += qtyUA;
      lot.coutTotal += cout;
    }

    const articles: AchatArticleRow[] = [...map.values()].map((art) => {
      const infos = this.articleInfos.getByArticleId(art.articleId)?.();
      const denom = infos?.denomination ?? art.articleId;
      const commandes: AchatCommandeDetail[] = [...art.commandes.values()]
        .map((cmd) => {
          const lots: AchatLotDetail[] = [...cmd.lots.values()].map((l) => ({
            lotId: l.lotId,
            lotNumero: l.lotNumero,
            coefUG: l.coefUG,
            qtyUG: l.qtyUG,
            qtyUA: l.qtyUA,
            puUG: l.qtyUG > 0 ? l.coutTotal / l.qtyUG : 0,
            puUA: l.qtyUA > 0 ? l.coutTotal / l.qtyUA : 0,
            coutTotal: l.coutTotal,
          })).sort((a, b) => b.qtyUG - a.qtyUG);
          return {
            commandeId: cmd.commandeId,
            numero: cmd.numero,
            dateKey: cmd.dateKey,
            coefUG: cmd.coefUG,
            qtyUG: cmd.qtyUG,
            qtyUA: cmd.qtyUA,
            puUGMoyen: cmd.qtyUG > 0 ? cmd.coutTotal / cmd.qtyUG : 0,
            puUAMoyen: cmd.qtyUA > 0 ? cmd.coutTotal / cmd.qtyUA : 0,
            coutTotal: cmd.coutTotal,
            lots,
          };
        })
        .sort((a, b) =>
          a.dateKey === b.dateKey
            ? a.numero.localeCompare(b.numero)
            : b.dateKey.localeCompare(a.dateKey),
        );

      const totalUG = commandes.reduce((s, c) => s + c.qtyUG, 0);
      const totalUA = commandes.reduce((s, c) => s + c.qtyUA, 0);
      const totalCout = commandes.reduce((s, c) => s + c.coutTotal, 0);
      return {
        articleId: art.articleId,
        articleDenomination: denom,
        uniteGestion: art.uniteGestion,
        uniteAchat: art.uniteAchat,
        qtyUG: totalUG,
        qtyUA: totalUA,
        coutTotal: totalCout,
        puUGMoyen: totalUG > 0 ? totalCout / totalUG : 0,
        puUAMoyen: totalUA > 0 ? totalCout / totalUA : 0,
        commandes,
      };
    });

    const data: AchatArticlesDialogData = {
      tiersDenomination: this.stats()?.tiers?.denomination ?? "",
      articles,
    };

    this.dialogsService.open(
      AchatArticlesDialogComponent,
      "min(95vw, 900px)",
      "fit-content",
      data,
    );
  }

  protected readonly Math = Math;
}
